import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AdminProvider } from './contexts/AdminContext';
import axios from 'axios';
import Header from './components/Header';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';

const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Product = lazy(() => import('./pages/Product'));
const News = lazy(() => import('./pages/News'));
const Contact = lazy(() => import('./pages/Contact'));
const Career = lazy(() => import('./pages/Career'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const ContentManager = lazy(() => import('./pages/admin/ContentManager'));
const NewsManager = lazy(() => import('./pages/admin/NewsManager'));
const ContactSubmissions = lazy(() => import('./pages/admin/ContactSubmissions'));
const CareerApplications = lazy(() => import('./pages/admin/CareerApplications'));
const CareerConfig = lazy(() => import('./pages/admin/CareerConfig'));
const PopupManager = lazy(() => import('./pages/admin/PopupManager'));
const SiteSettings = lazy(() => import('./pages/admin/SiteSettings'));
const MediaLibrary = lazy(() => import('./pages/admin/MediaLibrary'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const Profile = lazy(() => import('./pages/admin/Profile'));

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Loader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
    <div style={{
      width: '40px', height: '40px', border: '4px solid #e0e0e0',
      borderTop: '4px solid #01940F', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const Logo = () => {
  const [logoUrl, setLogoUrl] = useState('');
  useEffect(() => {
    axios.get(`${API_BASE}/api/settings`).then(res => {
      if (res.data.logo?.value) {
        const val = res.data.logo.value;
        setLogoUrl(val.startsWith('/uploads/') ? `${API_BASE}${val}` : val);
      }
    }).catch(() => {});
  }, []);
  if (!logoUrl) return null;
  return (
    <div className="logo">
      <a href="/"><img src={logoUrl} alt="logo" /></a>
    </div>
  );
};

function App() {
  return (
    <AdminProvider>
      <Router>
        <Suspense fallback={<Loader />}>
          <div className="App">
            <Routes>
              <Route path="/web-admin" element={<AdminLogin />} />
              <Route path="/admin" element={
                <PrivateRoute><AdminLayout /></PrivateRoute>
              }>
                <Route index element={<Dashboard />} />
                <Route path="profile" element={<Profile />} />
                <Route path="content" element={<ContentManager />} />
                <Route path="news" element={<NewsManager />} />
                <Route path="contacts" element={<ContactSubmissions />} />
                <Route path="career-apps" element={<CareerApplications />} />
                <Route path="career-config" element={<CareerConfig />} />
                <Route path="popups" element={<PopupManager />} />
                <Route path="settings" element={<SiteSettings />} />
                <Route path="media" element={<MediaLibrary />} />
                <Route path="users" element={<UserManagement />} />
              </Route>
              <Route path="/*" element={
                <>
                  <Logo />
                  <div className="container">
                    <Header />
                    <main>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/product" element={<Product />} />
                        <Route path="/news" element={<News />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/career" element={<Career />} />
                        <Route path="/thankyou" element={<ThankYou />} />
                      </Routes>
                    </main>
                    <Footer />
                  </div>
                </>
              } />
            </Routes>
          </div>
        </Suspense>
      </Router>
    </AdminProvider>
  );
}

export default App;
